import React, { useState, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { useCart } from 'contexts/CartContext';
import { useProducts } from 'contexts/ProductsContext';
import { useSizePreference } from 'contexts/SizePreference';
import { getSuggestions } from 'lib/suggestions';
import { shareProduct, getShareUrl } from 'lib/shareUtils';
import { getColorHex } from 'lib/colors';
import balloonSound from 'assets/windows_xp_sounds/Windows XP Balloon.wav';

function FabricCare({ description }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="pv__fabric-care">
      <div className="pv__fabric-care__label" onClick={() => setOpen(o => !o)} style={{ cursor: 'pointer' }}>
        <span className="pv__fabric-care__arrow">{open ? '▼' : '▶'}</span> Fabric & Care
      </div>
      {open && (
        <div className="pv__fabric-care__text">
          {description.replace(/&nbsp;/g, ' ').split(/(?=(?:Fabric|Fit|Care|Design):)/g).filter(s => s.trim() && !s.trim().match(/^_+$/)).map((s, i) => <div key={i}>{s.trim()}</div>)}
        </div>
      )}
    </div>
  );
}

function ProductViewer({ onClose, product, onShowDialog, onOpenCart, onOpenProduct, initialSelections = {}, onRegisterProductWindow, onUnregisterProductWindow, appId }) {
  const { addItem } = useCart();
  const { products: allProducts } = useProducts();
  const { sizePreference } = useSizePreference();
  const suggested = useMemo(() => getSuggestions(product || {}, allProducts, 2), [product?.id, allProducts]);
  const [currentImage, setCurrentImage] = useState(0);
  const autoSize = !initialSelections.size
    ? (product?.sizes?.length === 1 ? product.sizes[0] : (product?.sizes?.includes(sizePreference) ? sizePreference : ''))
    : '';
  const [selectedSize, setSelectedSize] = useState(initialSelections.size || autoSize);
  const [wasAutoSelected, setWasAutoSelected] = useState(!!autoSize && !initialSelections.size && product?.sizes?.length > 1);
  const [selectedColor, setSelectedColor] = useState(
    initialSelections.color || product?.defaultColor || product?.colors?.[0] || ''
  );
  const [quantity, setQuantity] = useState(initialSelections.quantity || 1);

  // Register this window's product+selections so parent can find/reuse it
  useEffect(() => {
    if (product && appId != null && onRegisterProductWindow) {
      onRegisterProductWindow(appId, product.id, selectedColor, selectedSize);
    }
  }, [appId, product?.id, selectedColor, selectedSize]);

  // Unregister on unmount
  useEffect(() => {
    return () => {
      if (appId != null && onUnregisterProductWindow) {
        onUnregisterProductWindow(appId);
      }
    };
  }, [appId]);

  if (!product) {
    return (
      <ViewerDiv>
        <div className="pv__empty">No product selected</div>
      </ViewerDiv>
    );
  }

  // Show images for selected color, fallback to all images
  let displayImages = product.images || [];
  if (product.colorImages && selectedColor && product.colorImages[selectedColor]) {
    displayImages = [...product.colorImages[selectedColor]];
  }
  // Always append size chart at the end if available
  if (product.sizeChart && !displayImages.includes(product.sizeChart)) {
    displayImages = [...displayImages, product.sizeChart];
  }

  const images = displayImages;
  const totalImages = images.length;
  const hasDiscount = product.compareAtPrice && product.compareAtPrice > product.price;
  const discountPercent = hasDiscount
    ? Math.round(((product.compareAtPrice - product.price) / product.compareAtPrice) * 100)
    : 0;

  const [zoomMode, setZoomMode] = useState(false);
  const [hoverPos, setHoverPos] = useState({ x: 50, y: 50 });
  const [zoomArmed, setZoomArmed] = useState(false);

  useEffect(() => {
    if (!zoomMode) return;
    function handleEsc(e) { if (e.key === 'Escape') setZoomMode(false); }
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [zoomMode]);

  function prevImage() {
    setCurrentImage(i => (i > 0 ? i - 1 : totalImages - 1));
  }
  function nextImage() {
    setCurrentImage(i => (i < totalImages - 1 ? i + 1 : 0));
  }

  function handleImageHover(e) {
    if (zoomMode) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setHoverPos({ x, y });
  }

  function handleZoomPan(e) {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setHoverPos({ x, y });
  }

  return (
    <ViewerDiv>
      {/* Full zoom overlay */}
      {zoomMode && (
        <div className="pv__zoom-overlay" onClick={() => setZoomMode(false)}>
          <div className="pv__zoom-container" onClick={() => setZoomMode(false)} onMouseMove={handleZoomPan}>
            <div
              className="pv__zoom-image"
              style={{
                backgroundImage: `url(${images[currentImage]})`,
                backgroundPosition: `${hoverPos.x}% ${hoverPos.y}%`,
              }}
            />
            <button className="pv__zoom-nav pv__zoom-nav--left" onClick={e => { e.stopPropagation(); prevImage(); }}>◀</button>
            <button className="pv__zoom-nav pv__zoom-nav--right" onClick={e => { e.stopPropagation(); nextImage(); }}>▶</button>
            <button className="pv__zoom-close" onClick={e => { e.stopPropagation(); setZoomMode(false); }}>✕</button>
            <div className="pv__zoom-counter">{currentImage + 1} / {totalImages}</div>
          </div>
        </div>
      )}
      <div className="pv__body">
        {/* Left: photo viewer */}
        <div className="pv__viewer">
          <div className="pv__screen-wrap">
            <button className="pv__screen-arrow pv__screen-arrow--left" onClick={prevImage}>◀</button>
            <div
              className={`pv__screen ${zoomArmed ? 'pv__screen--armed' : ''}`}
              onMouseMove={handleImageHover}
              onMouseLeave={() => setZoomArmed(true)}
              onClick={() => setZoomMode(true)}
              style={{ cursor: 'zoom-in' }}
            >
              {images[currentImage] && (
                <img
                  src={images[currentImage]}
                  alt={product.name}
                  style={{
                    transformOrigin: `${hoverPos.x}% ${hoverPos.y}%`,
                  }}
                  className="pv__screen-img"
                />
              )}
              {hasDiscount && (
                <div className="pv__discount-badge">-{discountPercent}%</div>
              )}
            </div>
            <button className="pv__screen-arrow pv__screen-arrow--right" onClick={nextImage}>▶</button>
          </div>

          <div className="pv__thumbs">
            {images.map((img, i) => (
              <div
                key={i}
                className={`pv__thumb ${i === currentImage ? 'pv__thumb--active' : ''}`}
                onClick={() => setCurrentImage(i)}
              >
                <img src={img} alt={`${product.name} ${i + 1}`} />
              </div>
            ))}
          </div>
        </div>

        {/* Right: product info */}
        <div className="pv__info">
          <div className="pv__info-content">
            <h3 className="pv__product-name">{product.name}</h3>

            <div className="pv__price-row">
              <span className="pv__product-price">₹{product.price.toLocaleString()}</span>
              {hasDiscount && (
                <>
                  <span className="pv__original-price">₹{product.compareAtPrice.toLocaleString()}</span>
                  <span className="pv__discount-tag">{discountPercent}% OFF</span>
                </>
              )}
            </div>

            <div className="pv__product-categories">
              {(product.categories || [product.category]).map(cat => {
                const label = cat === 'music' ? 'BANDS' : cat === 'games' ? 'GAMES' : cat === 'rep' ? 'REP' : 'GOOFY';
                const hoverName = cat === 'music' && product.band ? product.band : cat === 'games' && product.game ? product.game : cat === 'rep' && product.brand ? product.brand : null;
                return (
                  <button
                    key={cat}
                    className="pv__product-category"
                    title={hoverName ? `${label}: ${hoverName}` : label}
                    onClick={() => { window.dispatchEvent(new CustomEvent('binco-navigate', { detail: { category: cat, brand: hoverName } })); window.dispatchEvent(new CustomEvent('binco-focus')); }}
                  >
                    {label}{hoverName ? ` · ${hoverName}` : ''}
                  </button>
                );
              })}
            </div>

            {/* Color swatches */}
            {product.colors && product.colors.length > 1 && (
              <div className="pv__form-group">
                <label>Colour: {selectedColor}</label>
                <div className="pv__swatches">
                  {product.colors.map(color => (
                    <button
                      key={color}
                      className={`pv__swatch ${selectedColor === color ? 'pv__swatch--active' : ''}`}
                      style={{ backgroundColor: getColorHex(color) }}
                      onClick={() => { setSelectedColor(color); setCurrentImage(0); }}
                      title={color}
                    />
                  ))}
                </div>
              </div>
            )}

            <div className="pv__size-qty-row">
              <div className="pv__form-group pv__form-group--size">
                <label>Size: {wasAutoSelected && <span className="pv__auto-tag">auto-selected</span>}</label>
                <select
                  className={!selectedSize ? 'pv__size-select--empty' : ''}
                  value={selectedSize}
                  onChange={e => { setSelectedSize(e.target.value); setWasAutoSelected(false); }}
                >
                  <option value="">Select size</option>
                  {(product.sizes || []).map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div className="pv__form-group pv__form-group--qty">
                <label>Qty:</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={quantity}
                  onChange={e => setQuantity(Number(e.target.value))}
                />
              </div>
            </div>

            {product.description && (
              <FabricCare description={product.description} />
            )}

            <button className="pv__add-to-cart" onClick={() => {
              if (!selectedSize) {
                onShowDialog && onShowDialog('error', 'Please select a size before adding to cart.');
                return;
              }
              addItem(product, selectedSize, quantity, selectedColor);
              if (typeof window !== 'undefined' && window.gtag) {
                window.gtag('event', 'conversion', {
                  send_to: 'AW-18118827880/OilZCNyh0aIcEOi-3b9D',
                  value: product.price * quantity,
                  currency: 'INR',
                });
              }
              try { new Audio(balloonSound).play().catch(() => {}); } catch(e) {}
              onOpenCart && onOpenCart();
            }}>
              Add to Cart
            </button>

            <button className="pv__buy-now" onClick={() => {
              if (!selectedSize) {
                onShowDialog && onShowDialog('error', 'Please select a size before purchasing.');
                return;
              }
              addItem(product, selectedSize, quantity, selectedColor);
              try { new Audio(balloonSound).play().catch(() => {}); } catch(e) {}
              onOpenCart && onOpenCart();
            }}>
              Buy Now
            </button>

            <div className="pv__share-row">
              <button className="pv__share pv__share--primary" onClick={async () => {
                try {
                  await navigator.clipboard.writeText(getShareUrl(product));
                  onShowDialog && onShowDialog('success', 'Link copied to clipboard!');
                } catch { }
              }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="pv__share-icon pv__share-icon--link"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>
                Copy Link
              </button>
              <button className="pv__share pv__share--primary" onClick={async () => {
                const result = await shareProduct(product);
                if (result === 'copied') onShowDialog && onShowDialog('success', 'Link copied to clipboard!');
              }}>
                Share
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="pv__share-icon"><path d="M5 12h14"/><path d="M12 5l7 7-7 7"/></svg>
              </button>
            </div>

            {/* Suggested products */}
            {suggested.length > 0 && (
              <div className="pv__suggested">
                <div className="pv__suggested-label">You might also like</div>
                <div className="pv__suggested-grid">
                  {suggested.map(p => (
                    <div
                      key={p.id}
                      className="pv__suggested-item"
                      onClick={() => onOpenProduct && onOpenProduct(p)}
                    >
                      <div className={`pv__suggested-img ${p.isDualPrint ? 'pv__suggested-img--dual' : ''}`}>
                        <img className="pv__suggested-img-front" src={p.images[0]} alt={p.name} />
                        {p.isDualPrint && (() => {
                          const dc = p.defaultColor;
                          const backImg = dc && ((p.colorBackImages && p.colorBackImages[dc]) || (p.colorImages && p.colorImages[dc]?.[1]));
                          return backImg ? <img className="pv__suggested-img-back" src={backImg} alt={p.name + ' back'} /> : null;
                        })()}
                      </div>
                      <div className="pv__suggested-name">{p.name}</div>
                      <div className="pv__suggested-price">₹{p.price.toLocaleString()}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </ViewerDiv>
  );
}

const ViewerDiv = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #1a1a1a;
  color: #c0c0c0;
  font-family: Tahoma, sans-serif;
  font-size: 11px;
  overflow: hidden;

  .pv__empty {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: #666;
    font-size: 14px;
  }

  .pv__body {
    flex: 1;
    display: flex;
    overflow: hidden;
  }

  .pv__viewer {
    flex: 1;
    display: flex;
    flex-direction: column;
    background: #111;
    min-width: 0;
  }

  .pv__screen {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #000;
    overflow: hidden;
    position: relative;
    min-width: 0;
    .pv__screen-img {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
      transition: transform 0.15s ease-out;
    }
    &.pv__screen--armed:hover .pv__screen-img {
      transform: scale(2);
    }
  }

  /* Full zoom overlay */
  .pv__zoom-overlay {
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(0, 0, 0, 0.85);
    z-index: 100;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }
  .pv__zoom-container {
    position: relative;
    width: 90%;
    height: 90%;
    cursor: zoom-out;
  }
  .pv__zoom-image {
    width: 100%;
    height: 100%;
    background-size: 150%;
    background-repeat: no-repeat;
  }
  .pv__zoom-nav {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background: rgba(0, 0, 0, 0.6);
    border: 1px solid #555;
    color: #ccc;
    font-size: 18px;
    padding: 12px 10px;
    cursor: pointer;
    z-index: 2;
    &:hover { background: rgba(0, 0, 0, 0.8); color: #f5a623; }
  }
  .pv__zoom-nav--left { left: 8px; }
  .pv__zoom-nav--right { right: 8px; }
  .pv__zoom-close {
    position: absolute;
    top: 8px;
    right: 8px;
    background: rgba(0, 0, 0, 0.6);
    border: 1px solid #555;
    color: #ccc;
    font-size: 16px;
    padding: 6px 10px;
    cursor: pointer;
    z-index: 2;
    &:hover { color: #f5a623; }
  }
  .pv__zoom-counter {
    position: absolute;
    bottom: 8px;
    left: 50%;
    transform: translateX(-50%);
    color: #999;
    font-size: 11px;
    background: rgba(0, 0, 0, 0.6);
    padding: 3px 10px;
  }

  .pv__discount-badge {
    position: absolute;
    top: 8px;
    left: 8px;
    background: #e53935;
    color: white;
    font-size: 11px;
    font-weight: bold;
    padding: 3px 8px;
    border-radius: 1px;
  }

  .pv__screen-wrap {
    flex: 1;
    display: flex;
    align-items: stretch;
    min-height: 0;
  }
  .pv__screen-arrow {
    background: rgba(0, 0, 0, 0.4);
    border: none;
    color: #888;
    font-size: 14px;
    width: 28px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    &:hover { color: #f5a623; background: rgba(0, 0, 0, 0.6); }
  }

  .pv__thumbs {
    display: flex;
    gap: 4px;
    padding: 6px 8px;
    background: #1a1a1a;
    border-top: 1px solid #333;
    overflow-x: auto;
    justify-content: center;
  }
  .pv__thumb {
    width: 48px;
    height: 48px;
    flex-shrink: 0;
    border: 2px solid transparent;
    cursor: pointer;
    opacity: 0.5;
    background: #000;
    &:hover { opacity: 0.8; }
    &--active { border-color: #f5a623; opacity: 1; }
    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  .pv__info {
    width: 280px;
    min-width: 280px;
    background: #1e1e1e;
    border-left: 1px solid #333;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
  }

  .pv__info-content {
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .pv__product-name {
    font-size: 13px;
    font-weight: bold;
    color: #e0e0e0;
    margin: 0;
    line-height: 1.3;
  }

  .pv__price-row {
    display: flex;
    align-items: baseline;
    gap: 6px;
    flex-wrap: wrap;
  }
  .pv__product-price {
    font-size: 18px;
    font-weight: bold;
    color: #f5a623;
  }
  .pv__original-price {
    font-size: 13px;
    color: #666;
    text-decoration: line-through;
  }
  .pv__discount-tag {
    font-size: 10px;
    font-weight: bold;
    color: #4caf50;
    background: rgba(76, 175, 80, 0.1);
    padding: 1px 5px;
    border: 1px solid rgba(76, 175, 80, 0.3);
  }

  .pv__product-categories {
    display: flex;
    gap: 4px;
    flex-wrap: wrap;
  }
  .pv__product-category {
    font-size: 9px;
    color: #888;
    letter-spacing: 2px;
    padding: 2px 6px;
    background: #2a2a2a;
    border: 1px solid #333;
    display: inline-block;
    width: fit-content;
    cursor: pointer;
    font-family: Tahoma, sans-serif;
    &:hover { color: #f5a623; border-color: #f5a623; }
  }

  /* Color swatches */
  .pv__swatches {
    display: flex;
    gap: 5px;
    flex-wrap: wrap;
    margin-top: 2px;
  }
  .pv__swatch {
    width: 22px;
    height: 22px;
    border: 2px solid #444;
    cursor: pointer;
    padding: 0;
    transition: border-color 0.12s;
    &:hover {
      border-color: #999;
    }
    &--active {
      border-color: #f5a623;
      box-shadow: 0 0 0 1px #f5a623;
    }
  }

  .pv__size-qty-row {
    display: flex;
    gap: 10px;
    align-items: flex-end;
  }
  .pv__form-group {
    display: flex;
    flex-direction: column;
    gap: 3px;
    label {
      color: #999;
      font-size: 10px;
    }
    select, input {
      background: #111;
      border: 1px solid #444;
      color: #ccc;
      padding: 4px 6px;
      font-size: 11px;
      font-family: Tahoma, sans-serif;
    }
    select.pv__size-select--empty {
      border-color: #f5a623;
      color: #f5a623;
      animation: pv-size-pulse 1.6s ease-in-out infinite;
    }
    &--size { flex: 1; }
    &--qty { width: 55px; }
    input[type="number"] {
      width: 50px;
    }
  }
  @keyframes pv-size-pulse {
    0%, 100% { box-shadow: 0 0 0 rgba(245, 166, 35, 0); }
    50% { box-shadow: 0 0 8px rgba(245, 166, 35, 0.5); }
  }
  .pv__fabric-care {
    border: 1px solid #333;
    background: #1a1a1a;
    padding: 8px 10px;
    margin-top: 4px;
  }
  .pv__fabric-care__label {
    font-size: 9px;
    color: #f5a623;
    letter-spacing: 1.5px;
    font-weight: bold;
    margin-bottom: 4px;
    text-transform: uppercase;
    &:hover { color: #ffcc66; }
  }
  .pv__fabric-care__arrow {
    font-size: 7px;
    margin-right: 4px;
  }
  .pv__fabric-care__text {
    font-size: 10px;
    color: #999;
    line-height: 1.5;
    white-space: pre-line;
  }

  /* Suggestions */
  .pv__suggested {
    border-top: 1px solid #333;
    padding-top: 10px;
  }
  .pv__suggested-label {
    font-size: 9px;
    color: #777;
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: 8px;
  }
  .pv__suggested-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }
  .pv__suggested-item {
    cursor: pointer;
    border: 1px solid transparent;
    padding: 4px;
    overflow: hidden;
    &:hover {
      border-color: #f5a623;
      background: rgba(245, 166, 35, 0.04);
    }
  }
  .pv__suggested-img {
    width: 100%;
    height: 0;
    padding-bottom: 133%;
    position: relative;
    background: #111;
    border: 1px solid #333;
    overflow: hidden;
    .pv__suggested-img-front { position: absolute; top: 0; left: 0; width: 100%; height: 100%; object-fit: cover; }
    .pv__suggested-img-back {
      position: absolute; top: 0; left: 0; width: 100%; height: 100%; object-fit: cover;
      opacity: 0;
    }
    &--dual .pv__suggested-img-back {
      animation: pv-dual-flip 6s ease-in-out infinite;
    }
    &--dual::after {
      content: '⇆ DUAL';
      position: absolute;
      top: 4px;
      right: 4px;
      background: rgba(0,0,0,0.6);
      color: #f5a623;
      font-size: 7px;
      padding: 1px 4px;
      border-radius: 2px;
      letter-spacing: 0.5px;
      pointer-events: none;
      z-index: 1;
    }
  }
  @keyframes pv-dual-flip {
    0%, 45% { opacity: 0; }
    50%, 95% { opacity: 1; }
    100% { opacity: 0; }
  }
  .pv__suggested-name {
    font-size: 10px;
    color: #ccc;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-top: 4px;
  }
  .pv__suggested-price {
    font-size: 10px;
    color: #f5a623;
    font-weight: bold;
  }

  .pv__share-row {
    display: flex;
    gap: 6px;
  }
  .pv__share {
    flex: 1;
    background: linear-gradient(180deg, #3571c4 0%, #2056a0 100%);
    border: 1px solid #173d6e;
    border-top-color: #5a9ae0;
    border-left-color: #5a9ae0;
    color: #fff;
    padding: 7px 12px;
    cursor: pointer;
    font-size: 11px;
    font-weight: bold;
    font-family: Tahoma, sans-serif;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    box-shadow: inset 1px 1px 0 rgba(255,255,255,0.2), inset -1px -1px 0 rgba(0,0,0,0.15), 1px 1px 2px rgba(0,0,0,0.4);
    &:hover {
      background: linear-gradient(180deg, #4080d0 0%, #2a65b5 100%);
      border-top-color: #6aabe8;
      border-left-color: #6aabe8;
    }
    &:active {
      background: linear-gradient(180deg, #1a4a8a 0%, #2056a0 100%);
      border-top-color: #173d6e;
      border-left-color: #173d6e;
      border-bottom-color: #5a9ae0;
      border-right-color: #5a9ae0;
      box-shadow: inset 2px 2px 3px rgba(0,0,0,0.3), 0 0 0 rgba(0,0,0,0);
    }
  }
  .pv__share-icon {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
    &--link { width: 11px; height: 11px; }
  }

  .pv__auto-tag {
    font-size: 8px;
    color: #4caf50;
    margin-left: 4px;
    font-style: italic;
  }

  .pv__add-to-cart {
    background: linear-gradient(180deg, #f5a623 0%, #c8870e 100%);
    border: 1px solid #8a5c00;
    border-top-color: #ffd06a;
    border-left-color: #ffd06a;
    color: #000;
    padding: 7px 12px;
    cursor: pointer;
    font-weight: bold;
    font-size: 11px;
    font-family: Tahoma, sans-serif;
    box-shadow: inset 1px 1px 0 rgba(255,255,255,0.35), inset -1px -1px 0 rgba(0,0,0,0.2), 1px 1px 2px rgba(0,0,0,0.4);
    &:hover {
      background: linear-gradient(180deg, #ffbe3d 0%, #d4911a 100%);
    }
    &:active {
      background: linear-gradient(180deg, #b07510 0%, #c8870e 100%);
      border-top-color: #8a5c00;
      border-left-color: #8a5c00;
      border-bottom-color: #ffd06a;
      border-right-color: #ffd06a;
      box-shadow: inset 2px 2px 3px rgba(0,0,0,0.3), 0 0 0 rgba(0,0,0,0);
    }
  }
  .pv__buy-now {
    background: linear-gradient(180deg, #3a3a3a 0%, #222 100%);
    border: 1px solid #111;
    border-top-color: #555;
    border-left-color: #555;
    color: #ccc;
    padding: 7px 12px;
    cursor: pointer;
    font-weight: bold;
    font-size: 11px;
    font-family: Tahoma, sans-serif;
    box-shadow: inset 1px 1px 0 rgba(255,255,255,0.1), inset -1px -1px 0 rgba(0,0,0,0.2), 1px 1px 2px rgba(0,0,0,0.4);
    &:hover {
      background: linear-gradient(180deg, #444 0%, #2a2a2a 100%);
      color: #f5a623;
      border-top-color: #666;
      border-left-color: #666;
    }
    &:active {
      background: linear-gradient(180deg, #1a1a1a 0%, #2a2a2a 100%);
      border-top-color: #111;
      border-left-color: #111;
      border-bottom-color: #555;
      border-right-color: #555;
      box-shadow: inset 2px 2px 3px rgba(0,0,0,0.4), 0 0 0 rgba(0,0,0,0);
    }
  }
`;

export default ProductViewer;
